import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router";
import { AuthShell } from "@/components/auth/AuthShell";
import { errorMessage } from "@/components/auth/issues";
import { Button, TextField } from "@/components/ui/FormControls";
import { api } from "@/lib/api";
import { useAuth } from "@/store/auth";

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function submit(event: FormEvent) {
    event.preventDefault(); setBusy(true); setError(null); setNotice(null);
    try {
      const response = await api<{ message: string }>("/auth/password-reset/request", { method: "POST", json: { email: email.trim() } });
      setNotice(response.message);
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  }
  return <AuthShell title="비밀번호 찾기" sub="가입한 이메일로 비밀번호 재설정 링크를 받습니다. 교육용·기업용 계정 모두 사용할 수 있습니다." footer={<Link to="/login">로그인으로 돌아가기</Link>}>
    <form className="auth-form" onSubmit={submit}>
      <TextField label="가입 이메일" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      {notice && <p role="status" className="note note-ok text-label">{notice}</p>}
      {error && <p role="alert" className="note note-danger text-label">{error}</p>}
      <Button type="submit" variant="primary" fullWidth disabled={busy || !email.trim()}>{busy ? "요청 중…" : "재설정 링크 받기"}</Button>
    </form>
  </AuthShell>;
}

export function ResetPasswordPage() {
  const [token] = useState(() => new URLSearchParams(window.location.hash.slice(1)).get("token") ?? "");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmationError, setConfirmationError] = useState<string | null>(null);
  useEffect(() => { window.history.replaceState(window.history.state, "", window.location.pathname); }, []);
  async function submit(event: FormEvent) {
    event.preventDefault(); setError(null); setConfirmationError(null);
    if (password !== confirmation) { setConfirmationError("두 비밀번호가 일치하지 않습니다"); return; }
    setBusy(true);
    try {
      await api("/auth/password-reset/complete", { method: "POST", json: { token, password } });
      await useAuth.getState().load();
      setDone(true); setPassword(""); setConfirmation("");
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); }
  }
  return <AuthShell title="새 비밀번호 설정" sub="재설정 링크는 발송 후 30분 동안 한 번만 사용할 수 있습니다." footer={<Link to="/login">로그인으로 돌아가기</Link>}>
    {done ? <p role="status" className="note note-ok text-label">비밀번호를 변경했습니다. 기존 로그인은 모두 해제되었습니다. 새 비밀번호로 로그인해 주세요.</p> : !token ? <p className="note note-warn text-label">메일에 있는 재설정 링크로 접속해 주세요. <Link to="/forgot-password">새 링크 요청</Link></p> : <form className="auth-form" onSubmit={submit}>
      <TextField label="새 비밀번호" type="password" autoComplete="new-password" required minLength={8} maxLength={128} hint="8~128자" value={password} onChange={(e) => setPassword(e.target.value)} />
      <TextField label="새 비밀번호 확인" type="password" autoComplete="new-password" required value={confirmation} onChange={(e) => { setConfirmation(e.target.value); setConfirmationError(null); }} error={confirmationError} />
      {error && <p role="alert" className="note note-danger text-label">{error} <Link to="/forgot-password">새 링크 요청</Link></p>}
      <Button type="submit" variant="primary" fullWidth disabled={busy}>{busy ? "변경 중…" : "비밀번호 변경"}</Button>
    </form>}
  </AuthShell>;
}
